import React, { useState } from 'react';
import CardInfoOverlay from '../../common/Card/CardInfoOverlay';
import { getSetName } from '../../../utils/sets';

/**
 * The trade itself, as both the dashboard tickets and the trade
 * history page draw it: "YOU GET" over "YOU GIVE" as full-width
 * strips, card thumbnails with ×N copy badges, every thumb opening
 * the site's standard card info window. A strip with more cards
 * than fit overlaps them into a fan; hovering a card accordions it
 * out full-size. The strip never grows the ticket.
 */

// Copies collapse into one thumb with a ×N badge
export function groupCards(cards) {
    const seen = new Map();
    (cards || []).forEach(c => {
        const key = `${c.uuid}|${c.finish}`;
        if (seen.has(key)) seen.get(key).count++;
        else seen.set(key, { ...c, count: 1 });
    });
    return [...seen.values()];
}

/**
 * Card info window state + element. The overlay wants the full
 * catalog card plus prices and a set name - fetched lazily on the
 * first click, like every other card surface on the site.
 */
export function useCardInfo() {
    const [info, setInfo] = useState(null);

    const openCardInfo = async (c) => {
        let full = c;
        try {
            const res = await fetch(`/api/cards/${c.setCode}/${c.uuid}`);
            if (res.ok) full = await res.json();
        } catch { }
        let prices = {};
        try {
            const pr = await fetch(`/api/card-prices/${c.uuid}?setCode=${c.setCode}`);
            if (pr.ok) prices = await pr.json();
        } catch { }
        const setName = await getSetName(c.setCode);
        setInfo({
            card: { ...full, image_path: full.image_path || c.image_path },
            entry: { finish: c.finish, setcode: c.setCode },
            prices,
            setName,
        });
    };

    const cardInfoElement = (
        <CardInfoOverlay
            show={!!info}
            card={info?.card}
            entry={info?.entry}
            prices={info?.prices || {}}
            setDisplayName={info?.setName}
            showExtendedInfo={true}
            onClose={() => setInfo(null)}
        />
    );

    return { openCardInfo, cardInfoElement };
}

function Strip({ label, side, cards, onCardClick }) {
    const grouped = groupCards(cards);
    return (
        <div className={`tk-strip ${side}`}>
            <span className="tk-label">{label}</span>
            {grouped.length === 0 ? (
                <span className="tk-nothing">nothing on this side</span>
            ) : (
                <div className="tk-cards">
                    {grouped.map((c, i) => {
                        const hint = `${c.name || 'Card'}${c.finish && c.finish !== 'nonfoil' ? ` (${c.finish})` : ''}${c.count > 1 ? ` ×${c.count}` : ''}`;
                        return (
                            <button
                                key={`${c.uuid}-${c.finish}-${i}`}
                                className="tk-thumb"
                                title={hint}
                                onClick={(e) => { e.stopPropagation(); onCardClick && onCardClick(c); }}
                            >
                                {c.image_path
                                    ? <img src={c.image_path} alt={c.name} loading="lazy" />
                                    : <span className="tk-noimg">{c.name || 'Card'}</span>}
                                {c.count > 1 && <span className="tk-copies">×{c.count}</span>}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function TradeSides({ getCards, giveCards, onCardClick }) {
    return (
        <div className="tk-trade">
            <Strip label="You get" side="get" cards={getCards} onCardClick={onCardClick} />
            <Strip label="You give" side="give" cards={giveCards} onCardClick={onCardClick} />
        </div>
    );
}

export default TradeSides;
